import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import AuthModal from "./AuthModal";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // ✅ Initialize from sessionStorage for instant auth state
  const [user, setUser] = useState(() => {
    const cached = sessionStorage.getItem('authUser');
    return cached ? JSON.parse(cached) : null;
  });
  
  const [authLoading, setAuthLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  // ✅ Listen for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Check if logout event was triggered from another tab
      if (e.key === 'logout-event' && e.newValue) {
        
        sessionStorage.removeItem('authUser');
        setUser(null);
        window.location.href = '/'; // Redirect to landing page
      }
    };

    // Listen to storage events (only fires in OTHER tabs)
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const snap = await getDoc(userRef);
          
          const userData = snap.exists() ? snap.data() : {};
          const mergedUser = {
            uid: currentUser.uid,
            email: currentUser.email,
            photoURL: userData.photoURL || currentUser.photoURL,
            displayName: userData.displayName || currentUser.displayName
          };
          
          sessionStorage.setItem('authUser', JSON.stringify(mergedUser));
          setUser(mergedUser);
        } catch (error) {
          
          const basicUser = {
            uid: currentUser.uid,
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            displayName: currentUser.displayName
          };
          sessionStorage.setItem('authUser', JSON.stringify(basicUser));
          setUser(basicUser);
        }
      } else {
        sessionStorage.removeItem('authUser');
        setUser(null);
      }
      setAuthLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  const refreshUserData = async () => {
    if (auth.currentUser) {
      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const snap = await getDoc(userRef);
        
        if (snap.exists()) {
          const userData = snap.data();
          const mergedUser = {
            uid: auth.currentUser.uid,
            email: auth.currentUser.email,
            photoURL: userData.photoURL || auth.currentUser.photoURL,
            displayName: userData.displayName || auth.currentUser.displayName
          };
          
          sessionStorage.setItem('authUser', JSON.stringify(mergedUser));
          setUser(mergedUser);
        }
      } catch (error) {
        
      }
    }
  };

  const showAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  const hideAuthModal = () => setAuthModalOpen(false);

  const logout = async () => {
    try {
      await signOut(auth);
      
      // ✅ Clear sessionStorage in current tab
      sessionStorage.removeItem('authUser');
      
      // ✅ Notify other tabs via localStorage (triggers 'storage' event)
      localStorage.setItem('logout-event', Date.now().toString());
      
      // ✅ Clean up the logout event immediately
      setTimeout(() => {
        localStorage.removeItem('logout-event');
      }, 100);
      
      setUser(null);
      
      // ✅ Redirect current tab to landing page
      window.location.href = '/';
    } catch (error) {
      
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        authLoading, 
        showAuthModal, 
        hideAuthModal, 
        logout,
        refreshUserData
      }}
    >
      {children}
      <AuthModal
        isOpen={authModalOpen}
        onClose={hideAuthModal}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />
    </AuthContext.Provider>
  );
};